import React, { useState } from 'react';
import { format, differenceInMinutes } from 'date-fns';
import { 
  Calendar, 
  Users, 
  Clock, 
  DollarSign, 
  FileText,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Banknote,
  TrendingDown,
  Receipt,
  RotateCcw,
  Ban
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';

import { usePayrollRunDetails, useRunPayslips } from '@/hooks/payroll/usePayrollRuns';
import { useIndividualPayslipRollback } from '@/hooks/payroll/useIndividualPayslipRollback';
import { useCancelStalePayrollRun } from '@/hooks/payroll/useCancelStalePayrollRun';
import { usePayrollExport } from '@/hooks/payroll/usePayrollExport';
import { usePayslipLineItems } from '@/hooks/payslips';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { useHasPermission } from '@/hooks/permissions';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { IndividualPayslipRollbackDialog } from './IndividualPayslipRollbackDialog';
import { PayrollExportButton } from './PayrollExportButton';

interface PayrollRunDetailsProps {
  runId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const statusIcons = {
  draft: AlertCircle,
  processing: Clock,
  completed: CheckCircle2,
  failed: XCircle,
  rolled_back: XCircle,
};

// Sub-component for the expanded detail content
function PayslipDetailContent({ 
  payslipId, 
  netPay,
  formatCurrency 
}: { 
  payslipId: string;
  netPay: number;
  formatCurrency: (amount: number) => string;
}) {
  const { data: lineItems, isLoading: lineItemsLoading } = usePayslipLineItems(payslipId);

  // Categorize line items
  const earnings = lineItems?.filter(item => item.category === 'earnings') || [];
  const deductions = lineItems?.filter(item => item.category === 'deductions') || [];
  const taxes = lineItems?.filter(item => item.category === 'taxes') || [];
  const benefits = lineItems?.filter(item => item.category === 'benefits') || [];

  if (lineItemsLoading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Earnings */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-600 dark:text-green-400">
            <Banknote className="h-4 w-4 flex-shrink-0" />
            <span>Earnings</span>
          </div>
          {earnings.length === 0 ? (
            <p className="text-xs text-muted-foreground">No earnings</p>
          ) : (
            <div className="space-y-1.5">
              {earnings.map((item) => (
                <div key={item.id} className="text-xs">
                  <div className="text-muted-foreground mb-0.5">
                    {item.description || item.line_item_type}
                  </div>
                  <div className="font-medium text-green-600 dark:text-green-400">
                    {formatCurrency(Number(item.gross_amount || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Taxes */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-600 dark:text-red-400">
            <Receipt className="h-4 w-4 flex-shrink-0" />
            <span>Taxes</span>
          </div>
          {taxes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No taxes</p>
          ) : (
            <div className="space-y-1.5">
              {taxes.map((item) => (
                <div key={item.id} className="text-xs">
                  <div className="text-muted-foreground mb-0.5">
                    {item.description || item.line_item_type}
                  </div>
                  <div className="font-medium text-red-600 dark:text-red-400">
                    -{formatCurrency(Number(item.gross_amount || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deductions */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-orange-600 dark:text-orange-400">
            <TrendingDown className="h-4 w-4 flex-shrink-0" />
            <span>Deductions</span>
          </div>
          {deductions.length === 0 ? (
            <p className="text-xs text-muted-foreground">No deductions</p>
          ) : (
            <div className="space-y-1.5">
              {deductions.map((item) => (
                <div key={item.id} className="text-xs">
                  <div className="text-muted-foreground mb-0.5">
                    {item.description || item.line_item_type}
                  </div>
                  <div className="font-medium text-orange-600 dark:text-orange-400">
                    -{formatCurrency(Number(item.gross_amount || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400">
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span>Benefits</span>
          </div>
          {benefits.length === 0 ? (
            <p className="text-xs text-muted-foreground">No benefits</p>
          ) : (
            <div className="space-y-1.5">
              {benefits.map((item) => (
                <div key={item.id} className="text-xs">
                  <div className="text-muted-foreground mb-0.5">
                    {item.description || item.line_item_type}
                  </div>
                  <div className="font-medium text-blue-600 dark:text-blue-400">
                    {formatCurrency(Number(item.gross_amount || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Summary row */}
      <Separator />
      <div className="flex justify-between items-center text-sm">
        <span className="font-medium">Net Pay</span>
        <span className="font-bold text-lg">
          {formatCurrency(netPay)}
        </span>
      </div>
    </div>
  );
}

export function PayrollRunDetails({ runId, open, onOpenChange }: PayrollRunDetailsProps) {
  const [expandedPayslips, setExpandedPayslips] = useState<Set<string>>(new Set());
  const [selectedPayslipIds, setSelectedPayslipIds] = useState<Set<string>>(new Set());
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  
  const { data: run, isLoading: runLoading } = usePayrollRunDetails(runId || '');
  const { data: payslips, isLoading: payslipsLoading } = useRunPayslips(runId || '');
  const { currencyCode } = useWorkplaceCurrency();
  const { profile } = useAuth();
  const canExport = useHasPermission('export_timesheets');
  const rollbackMutation = useIndividualPayslipRollback();
  const cancelMutation = useCancelStalePayrollRun();
  const { exportRun } = usePayrollExport();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const togglePayslipExpanded = (payslipId: string) => {
    setExpandedPayslips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payslipId)) {
        newSet.delete(payslipId);
      } else {
        newSet.add(payslipId);
      }
      return newSet;
    });
  };

  const togglePayslipSelected = (payslipId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row expansion
    setSelectedPayslipIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payslipId)) {
        newSet.delete(payslipId);
      } else {
        newSet.add(payslipId);
      }
      return newSet;
    });
  };

  const toggleAllSelected = () => {
    if (!payslips) return;
    if (selectedPayslipIds.size === payslips.length) {
      setSelectedPayslipIds(new Set());
    } else {
      setSelectedPayslipIds(new Set(payslips.map((p: any) => p.id)));
    }
  };

  const handleRollbackConfirm = (payslipIds: string[], reason: string) => {
    if (!profile?.id) return;
    
    rollbackMutation.mutate(
      {
        payslipIds,
        reason,
        performedBy: profile.id,
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            setShowRollbackDialog(false);
            setSelectedPayslipIds(new Set());
            // If all payslips were rolled back, close the details dialog
            if (result.runStatus === 'rolled_back') {
              onOpenChange(false);
            }
          }
        },
      }
    );
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setExpandedPayslips(new Set());
      setSelectedPayslipIds(new Set());
    }
    onOpenChange(newOpen);
  };

  if (!runId) return null;

  const StatusIcon = run?.status ? statusIcons[run.status as keyof typeof statusIcons] : AlertCircle;
  const canRollback = run?.status === 'completed' && payslips && payslips.length > 0;
  const showSelection = (canRollback || canExport) && payslips && payslips.length > 0;
  // Allow cancel only for processing runs older than 10 minutes
  // Uses created_at as proxy since the run transitions to processing immediately after creation
  const isStaleProcessing = run?.status === 'processing' && run?.created_at
    ? differenceInMinutes(new Date(), new Date(run.created_at)) >= 10
    : false;
  const allSelected = payslips && payslips.length > 0 && selectedPayslipIds.size === payslips.length;
  const someSelected = selectedPayslipIds.size > 0;

  const handleCancelStaleRun = () => {
    if (!profile?.id || !runId) return;
    cancelMutation.mutate({
      runId,
      reason: 'Manually cancelled: stuck in processing state',
      performedBy: profile.id,
    }, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  // Build selected payslips data for dialog
  const selectedPayslipsData = payslips
    ?.filter((p: any) => selectedPayslipIds.has(p.id))
    .map((p: any) => ({
      id: p.id,
      employeeName: p.employee_name || 'Unknown',
      grossPay: Number(p.gross_pay || 0),
      netPay: Number(p.net_pay || 0),
    })) || [];

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl">Payroll Run Details</DialogTitle>
              {run && (
                <Badge 
                  variant={run.status === 'completed' ? 'default' : 
                          run.status === 'failed' ? 'destructive' : 'secondary'}
                  className="flex items-center gap-1"
                >
                  <StatusIcon className="h-3 w-3" />
                  {run.status}
                </Badge>
              )}
            </div>
            <DialogDescription>
              {run ? `Run #${run.run_number}` : 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {runLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : run ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Pay Period</CardTitle>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(run.start_date), 'MMM d, yyyy')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      to {format(new Date(run.end_date), 'MMM d, yyyy')}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Employees</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{run.payslips_generated || 0}</div>
                    <p className="text-xs text-muted-foreground">
                      of {run.total_employees_selected} selected
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Gross</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(Number(run.total_gross_pay || 0))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {Number(run.total_hours || 0).toFixed(2)} hours
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Net</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(Number(run.total_net_pay || 0))}
                    </div>
                    <p className="text-xs text-muted-foreground">After deductions</p>
                  </CardContent>
                </Card>
              </div>

              {/* Run Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Run Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Created By:</span>
                      <p className="text-muted-foreground">{run.creator_name || 'System'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Created At:</span>
                      <p className="text-muted-foreground">
                        {format(new Date(run.created_at), 'MMM d, yyyy HH:mm')}
                      </p>
                    </div>
                    {run.completed_at && (
                      <>
                        <div>
                          <span className="font-medium">Completed At:</span>
                          <p className="text-muted-foreground">
                            {format(new Date(run.completed_at), 'MMM d, yyyy HH:mm')}
                          </p>
                        </div>
                      </>
                    )}
                    {run.status === 'rolled_back' && (
                      <>
                        <div>
                          <span className="font-medium">Rolled Back By:</span>
                          <p className="text-muted-foreground">
                            {run.rollback_user_name || 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium">Rollback Reason:</span>
                          <p className="text-muted-foreground">{run.rollback_reason || 'N/A'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Stale processing run warning + cancel action */}
              {isStaleProcessing && (
                <Card className="border-destructive">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm">This run appears stuck in processing</p>
                          <p className="text-xs text-muted-foreground">
                            It has been processing for over 10 minutes. You can cancel it to clean up any orphaned data.
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleCancelStaleRun}
                        disabled={cancelMutation.isPending}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Run'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {run.status === 'processing' && !isStaleProcessing && (
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-muted-foreground animate-spin flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm">Payroll is being generated</p>
                        <p className="text-xs text-muted-foreground">
                          This may take a few minutes. The page will update automatically when complete.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Separator />

              {/* Payslips Table */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">Employee Payslips</h3>
                    <p className="text-xs text-muted-foreground">
                      {showSelection 
                        ? 'Select payslips to export or rollback, or click a row to view details'
                        : 'Click a row to view details'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canExport && payslips && payslips.length > 0 && (
                      <PayrollExportButton
                        onExport={(fmt) =>
                          exportRun.mutate({
                            runId: runId!,
                            format: fmt,
                            payslipIds: someSelected
                              ? Array.from(selectedPayslipIds)
                              : undefined,
                          })
                        }
                        isExporting={exportRun.isPending}
                        variant="sm"
                        label={someSelected ? `Export Selected (${selectedPayslipIds.size})` : 'Export All'}
                      />
                    )}
                    {canRollback && someSelected && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setShowRollbackDialog(true)}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Rollback Selected ({selectedPayslipIds.size})
                      </Button>
                    )}
                  </div>
                </div>
                {payslipsLoading ? (
                  <Skeleton className="h-64 w-full" />
                ) : (
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {showSelection && (
                            <TableHead className="w-[50px]">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={toggleAllSelected}
                                aria-label="Select all payslips"
                              />
                            </TableHead>
                          )}
                          <TableHead>Employee</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Gross Pay</TableHead>
                          <TableHead>Deductions</TableHead>
                          <TableHead>Net Pay</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {!payslips || payslips.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={showSelection ? 7 : 6} className="text-center py-8 text-muted-foreground">
                              No payslips found for this run
                            </TableCell>
                          </TableRow>
                        ) : (
                          payslips.map((payslip: any) => {
                            const isExpanded = expandedPayslips.has(payslip.id);
                            const isSelected = selectedPayslipIds.has(payslip.id);
                            return (
                              <React.Fragment key={payslip.id}>
                                <TableRow 
                                  className={cn(
                                    "cursor-pointer hover:bg-muted/50 transition-colors",
                                    isExpanded && "bg-muted/30",
                                    isSelected && "bg-destructive/10"
                                  )}
                                  onClick={() => togglePayslipExpanded(payslip.id)}
                                >
                                  {showSelection && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() => {
                                          setSelectedPayslipIds(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(payslip.id)) {
                                              newSet.delete(payslip.id);
                                            } else {
                                              newSet.add(payslip.id);
                                            }
                                            return newSet;
                                          });
                                        }}
                                        aria-label={`Select ${payslip.employee_name}`}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                      )}
                                      <div>
                                        <div className="font-medium">{payslip.employee_name}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {payslip.employee_email}
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="capitalize">
                                    {payslip.employee_role || 'N/A'}
                                  </TableCell>
                                  <TableCell>{Number(payslip.total_hours || 0).toFixed(2)}</TableCell>
                                  <TableCell>{formatCurrency(Number(payslip.gross_pay || 0))}</TableCell>
                                  <TableCell>
                                    {formatCurrency(Number(payslip.total_deductions || 0))}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {formatCurrency(Number(payslip.net_pay || 0))}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                                    <TableCell colSpan={showSelection ? 7 : 6} className="p-0">
                                      <PayslipDetailContent
                                        payslipId={payslip.id}
                                        netPay={Number(payslip.net_pay || 0)}
                                        formatCurrency={formatCurrency}
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Run not found
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Individual Rollback Confirmation Dialog */}
      <IndividualPayslipRollbackDialog
        selectedPayslips={selectedPayslipsData}
        totalPayslipsInRun={payslips?.length || 0}
        runNumber={run?.run_number || ''}
        open={showRollbackDialog}
        onOpenChange={setShowRollbackDialog}
        onConfirm={handleRollbackConfirm}
        isRollingBack={rollbackMutation.isPending}
        formatCurrency={formatCurrency}
      />
    </>
  );
}
